import { usePrimVoices, DebugMessage } from "primvoices-react";
import { motion } from "framer-motion";
import clsx from "clsx";
import { useState, useMemo, useEffect, useRef } from "react";

import { View, Text, Tooltip, Icon, JsonViewer } from "../base";
import { getEventColor, getEventIcon } from "../../utils/events";

const formatEventData = (message: DebugMessage) => {
  if (!message.data) return null;

  // Use JsonViewer for objects with more than 1 key, or for specific event types
  if ((typeof message.data === "object" && Object.keys(message.data).length > 1) || ["error", "start", "log", "context"].includes(message.name)) {
    return message.data;
  }

  // For simple data, return as formatted JSON string
  const jsonString = JSON.stringify(message.data, null, 2);
  return jsonString.replace(/\\n/g, "\n");
};

export const AgentMonitoring = () => {
  const { debugMessages } = usePrimVoices();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [collapsedTurns, setCollapsedTurns] = useState<Record<string | number, boolean>>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [userHasScrolled, setUserHasScrolled] = useState(false);

  // Handle scroll events to detect user scrolling
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold

      if (isAtBottom) {
        setUserHasScrolled(false);
        setShouldAutoScroll(true);
      } else {
        setUserHasScrolled(true);
        setShouldAutoScroll(false);
      }
    };

    container.addEventListener("scroll", handleScroll);

    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // Scroll to bottom when new messages arrive (only if user hasn't scrolled up)
  useEffect(() => {
    if (containerRef.current && debugMessages?.length && shouldAutoScroll && !userHasScrolled) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [debugMessages, shouldAutoScroll, userHasScrolled]);

  // Group messages by turn - memoized to prevent unnecessary recalculations
  const groupedMessages = useMemo(() => {
    if (!debugMessages) return {};

    return debugMessages
      .filter((message) => {
        return message.name !== "transcript";
      })
      .reduce<Record<string | number, { messages: DebugMessage[]; duration: number; latencies?: Record<string, number> }>>(
        (acc, message) => {
          const turn = `${message.turn ?? "unknown"}`;

          if (!acc[turn]) {
            acc[turn] = { messages: [], duration: 0, latencies: {} };
          }

          if (message.name === "turn_end") {
            acc[turn].duration = message.data.duration as number;
          } else if (message.name?.startsWith("metrics.latency.")) {
            const key = message.name.replace("metrics.latency.", "");
            const seconds = (message.data as any)?.value_s ?? (message.data as any)?.latency_s;
            const ms = typeof seconds === "number" ? Math.round(seconds * 1000) : undefined;
            if (ms !== undefined) {
              acc[turn].latencies = { ...(acc[turn].latencies || {}), [key]: ms };
            }
          } else {
            acc[turn].messages.push(message);
          }

          return acc;
        },
        {} as Record<string, { messages: DebugMessage[]; duration: number }>,
      );
  }, [debugMessages]);

  const toggleTurn = (turn: string | number) => {
    setCollapsedTurns((prev) => ({
      ...prev,
      [turn]: !prev[turn],
    }));
  };

  return (
    <View
      ref={containerRef}
      className="flex-1 overflow-y-auto"
    >
      {groupedMessages &&
        Object.entries(groupedMessages).map(([turn, { messages, duration, latencies }], turnIndex) => (
          <View key={turn}>
            <View
              className="sticky top-0 z-10 flex items-center bg-indigo-100 dark:bg-gray-800 cursor-pointer border-b border-gray-300 dark:border-gray-700"
              onClick={() => toggleTurn(turn)}
            >
              <View className="px-2 py-1 w-full bg-indigo-100 dark:bg-brand/20 flex justify-between items-center border-l-4 border-brand">
                <View className="flex items-center justify-start">
                  <Icon
                    name={collapsedTurns[turn] ? "chevron-right" : "chevron-down"}
                    size={16}
                    className="text-indigo-700 dark:text-indigo-200 mr-2"
                  />
                  <Text className="text-xs font-medium text-indigo-700 dark:text-indigo-200">Turn {turn === "unknown" ? "unknown" : turnIndex + 1}</Text>
                </View>
                <View className="flex items-center gap-2">
                  {latencies && (
                    <Tooltip
                      content={
                        <View>
                          <Row
                            label="Time to First Transcription"
                            textClass="text-purple-500"
                            dotClass="bg-purple-500"
                            value={latencies["stt_first_transcription_frame"]}
                          />
                          <Row
                            label="Time to First Speech Event"
                            textClass="text-blue-600"
                            dotClass="bg-blue-600"
                            value={latencies["handler_first_speech_event"]}
                          />
                          <Row
                            label="Time to First Audio"
                            textClass="text-yellow-600"
                            dotClass="bg-yellow-600"
                            value={latencies["tts_first_audio_frame"]}
                          />
                          <Row
                            label="End-to-End Turn Taking"
                            textClass="text-green-600"
                            dotClass="bg-green-600"
                            value={latencies["turn_taking"]}
                          />
                          <Row
                            label="Function Runtime"
                            textClass="text-cyan-400"
                            dotClass="bg-cyan-400"
                            value={Number(duration.toFixed(0))}
                          />
                        </View>
                      }
                      placement="bottom"
                    >
                      <View className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 select-none cursor-default">
                        <Icon
                          name="info"
                          size={12}
                          className="text-gray-500 dark:text-gray-400"
                        />
                        Turn Latencies
                      </View>
                    </Tooltip>
                  )}
                </View>
              </View>
            </View>

            {!collapsedTurns[turn] &&
              messages.map((message, index) => {
                // Use a more stable key that includes both turn and message index
                const messageKey = `${turn}-${index}-${message.name}`;
                const globalIndex = debugMessages?.indexOf(message) ?? index;
                const isBackground = message.data?.background === true;
                const backgroundTaskName = message.data?.background_task_name as string;

                return (
                  <motion.div
                    key={messageKey}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className={clsx(
                      "border-b border-gray-200 dark:border-gray-700",
                      message.name === "error"
                        ? "bg-red-50 dark:bg-red-900/20"
                        : message.type === "input"
                          ? "bg-blue-50 dark:bg-blue-900/20"
                          : isBackground
                            ? "bg-orange-50 dark:bg-orange-900/20"
                            : "bg-gray-50 dark:bg-gray-800/50",
                    )}
                  >
                    <View
                      className={clsx(
                        "w-full text-left p-3 cursor-pointer",
                        expandedIndex === globalIndex && "bg-gray-100 dark:bg-gray-700",
                        message.name === "error"
                          ? "border-l-4 border-l-red-600"
                          : message.type === "input"
                            ? "border-l-4 border-l-indigo-300 dark:border-l-indigo-400"
                            : isBackground
                              ? "border-l-4 border-l-orange-400 dark:border-l-orange-500"
                              : "border-l-4 border-l-gray-300 dark:border-l-gray-600",
                      )}
                      onClick={() => setExpandedIndex(expandedIndex === globalIndex ? null : globalIndex)}
                    >
                      <View className="flex items-center gap-3">
                        <View className="flex items-center gap-2">
                          <Icon
                            name={expandedIndex === globalIndex ? "chevron-down" : "chevron-right"}
                            size={16}
                            className="text-gray-400"
                          />
                          <Icon
                            name={getEventIcon(message.name, message.data)}
                            size={20}
                            className={getEventColor(message.name, message.type)}
                          />
                        </View>
                        <View className="flex gap-1 items-center justify-between w-full">
                          <Text className={clsx("font-medium capitalize", message.name === "error" && "text-red-600 font-bold")}>
                            {message.name.replace(/_/g, " ")}
                            {message.name === "text" && (
                              <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                                via {message.data.source === "speech" ? "speech" : "text input"}
                              </span>
                            )}
                          </Text>
                          {isBackground && backgroundTaskName && (
                            <View className="flex items-center gap-1">
                              <Icon
                                name="clock"
                                size={16}
                                className="text-orange-500 dark:text-orange-400"
                              />
                              <Text className="text-xs text-orange-600 dark:text-orange-400 font-semibold">Background:</Text>
                              <Text className="text-xs text-orange-600 dark:text-orange-400">
                                {backgroundTaskName?.includes("-") ? backgroundTaskName.split("-")[0] : backgroundTaskName}
                              </Text>
                            </View>
                          )}
                        </View>
                      </View>
                    </View>

                    {expandedIndex === globalIndex && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <View className="p-3 border-t border-gray-200 dark:border-gray-700">
                          {typeof formatEventData(message) === "object" ? (
                            <JsonViewer
                              data={formatEventData(message)}
                              className="text-sm"
                            />
                          ) : (
                            <Text className="text-sm whitespace-pre-wrap">{formatEventData(message) as string}</Text>
                          )}
                        </View>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
          </View>
        ))}
    </View>
  );
};

const Row = ({ label, value, textClass, dotClass }: { label: string; value?: number; textClass: string; dotClass: string }) => (
  <View className="flex items-center gap-2 py-0.5">
    <View className={clsx("w-2 h-2 rounded-full", dotClass)} />
    <span className={clsx("text-xs", textClass)}>
      {label}: {value !== undefined ? `${value} ms` : "—"}
    </span>
  </View>
);
